import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { FormOfFinancing } from '@chancenwerk/_cms/financing/entities/FormOfFinancing.js';
import { FormOfFinancingBuT } from '@chancenwerk/_cms/financing/entities/FormOfFinancingBuT.js';
import { FormOfFinancingMitgliedsbeitrag } from '@chancenwerk/_cms/financing/entities/FormOfFinancingMitgliedsbeitrag.js';
import { FormOfFinancingStiftungsgeld } from '@chancenwerk/_cms/financing/entities/FormOfFinancingStiftungsgeld.js';
import { Product, PRODUCT_BLACKLIST } from '@chancenwerk/_cms/products/entities/Product.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import { ProductSCH } from '@chancenwerk/_cms/products/entities/ProductSCH.js';
import { AcquisitionRecord } from '@chancenwerk/db/AcquisitionRecord.js';
import { BuTStatus, LogicExpression, StudentType } from '@chancenwerk/db/enums/enums.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import { RAGFundingInformation } from '@chancenwerk/funding/entities/RAGFundingInformation.js';
import { resolveKPFRelationshipsForRole } from '@chancenwerk/master-data-management/kpf/api/resolveKPFRelationshipsForRole.js';
import { Partner, PARTNER_BLACKLIST } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { ExternalContactPerson } from '@chancenwerk/master-data-management/users/entities/ExternalContactPerson.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { kpfTokenChangeInformation, kpfTokenCreationInformation } from '@chancenwerk/server/mailTemplates.js';
import { chancenWerkMailer } from '@chancenwerk/server/mailer.js';
import { DateTime } from 'luxon';
import type { InsertOneResult, UpdateResult } from 'mongodb';
import { PERSON_BLACKLIST } from '../../users/entities/ChancenwerkUser.js';
import {
  FormOfFinancingLogicExpression,
  FormOfFinancingLogicExpressionArrayUnion,
} from './FormOfFinancingLogicExpression.js';

export const KPF_BLACKLIST = {
  _class: 1,
  token: 1,
  schuKos: PERSON_BLACKLIST,
  leKos: PERSON_BLACKLIST,
  ansprechPersonExtern: PERSON_BLACKLIST,
  butAnsprechPersonExtern: PERSON_BLACKLIST,
  personsStrategisch: PERSON_BLACKLIST,
  personsOperativ: PERSON_BLACKLIST,
  personsVerwaltung: PERSON_BLACKLIST,
  personsBUTOperativ: PERSON_BLACKLIST,
  formOfFinanzingLogicString: 1,
  school: PARTNER_BLACKLIST,
  usStudents: 1,
  osStudents: 1,
  schoolID: 1,
  substituteKPFIDs: 1,
  substituteKPFs: 1,
  tokenAccessSoftLimitMailSent: 1,
  tokenAccessHardLimitMailSent: 1,
  generateLevumiTestReminderForSchuKos: 1,
  attendanceLogsCount: 1,
  ragPartnerInformation: 1,
  acquisitionRecord: 1,
  produkt: PRODUCT_BLACKLIST,
  schoolSupport: PERSON_BLACKLIST,
};

/**
 * @entity KPF
 */
@Entity()
export class KPF<T extends Product = Product> extends MongoEntity {
  public static override _class = 'KPF';
  public static override _collectionName = 'kpfs';

  public static override get allFields() {
    return selectionSet(() => this, 3);
  }

  public get stringRepresentation(): string {
    return `${this.kuerzel} ${this.school?.name ? `(${this.school.name})` : ''}`;
  }

  @Field(type => ID)
  public schoolID?: string;

  @DataLoadedField(type => Partner, ({ schoolID }) => schoolID)
  public school: Maybe<Partner>;

  @Field(type => Product)
  public produkt: Maybe<T>;

  @Field(type => String)
  public butStatus: Maybe<BuTStatus>;

  @Field(type => String)
  public kostenstellennummer?: string;

  public get kuerzel() {
    return (this.school?.kuerzel ?? 'X_X') + '_' + (this.produkt?.kuerzel ?? 'X');
  }

  @Field(type => String)
  public kommentar?: string;

  @Field(type => String)
  public token?: string;

  @validate<KPF<T>>(({ value, key, target }) => {
    if (value && target.endDatum && value > target.endDatum)
      throw new ValidationError(`Das Startdatum muss vor dem Enddatum liegen.`, { key, target });
  })
  @Field(type => DateTime)
  public startDatum: Maybe<DateTime>;

  @validate<KPF<T>>(({ value, key, target }) => {
    if (value && target.startDatum && value < target.startDatum)
      throw new ValidationError(`Das Enddatum muss hinter dem Startdatum liegen.`, { key, target });
  })
  @Field(type => DateTime)
  public endDatum: Maybe<DateTime>;

  @Field(type => AcquisitionRecord, {
    resolve: function (this: KPF<T>) {
      return async () => {
        const res = (await loadMongoData(AcquisitionRecord, { kpfID: this._id })).at(0);
        return res as AcquisitionRecord;
      };
    },
  })
  public acquisitionRecord: Maybe<AcquisitionRecord>;

  @Field(type => ExternalContactPerson, {
    resolve: function (this: KPF<T>) {
      return async () =>
        (await resolveKPFRelationshipsForRole<ExternalContactPerson, T>(this, Role.EXTERNAL_CONTACT)).at(0);
    },
  })
  public ansprechPersonExtern?: ExternalContactPerson;

  @Field(type => ExternalContactPerson, {
    resolve: function (this: KPF<T>) {
      return async () =>
        (await resolveKPFRelationshipsForRole<ExternalContactPerson, T>(this, Role.EXTERNAL_CONTACT_BUT)).at(0);
    },
  })
  public butAnsprechPersonExtern?: ExternalContactPerson;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_STRATEGY);
    },
  })
  public personsStrategisch: Maybe<Mitarbeiter[]>;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_OPERATIV);
    },
  })
  public personsOperativ: Maybe<Mitarbeiter[]>;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_ADMINISTRATION);
    },
  })
  public personsVerwaltung: Maybe<Mitarbeiter[]>;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.INTERNAL_KPF_BUT_OPERATIV);
    },
  })
  public personsBUTOperativ: Maybe<Mitarbeiter[]>;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.SCHOOL_SUPPORT);
    },
  })
  public schoolSupport: Maybe<Mitarbeiter[]>;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.SCHUKO);
    },
  })
  public schuKos: Maybe<Mitarbeiter[]>;

  @Field(type => [String], { default: v => v ?? [] })
  public substituteKPFIDs: Maybe<string[]>;

  @DataLoadedField(type => [KPF], ({ substituteKPFIDs }) => substituteKPFIDs ?? [])
  public substituteKPFs: Maybe<Array<KPF<T>>>;

  @Field(type => [Mitarbeiter], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Mitarbeiter, T>(this, Role.LEKO);
    },
  })
  public leKos: Maybe<Mitarbeiter[]>;

  @Field(type => [Student], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Student, T>(this, Role.STUDENT);
    },
  })
  public usStudents: Maybe<Student[]>;

  @Field(type => [Student], {
    resolve: function (this: KPF<T>) {
      return async () => resolveKPFRelationshipsForRole<Student, T>(this, Role.STUDENT, StudentType.OS);
    },
  })
  public osStudents: Maybe<Student[]>;

  @Field(type => Boolean, { default: v => v ?? false })
  public tokenAccessForExternalsEnabled?: boolean;

  @Field(type => String)
  public accessTokenForExternals?: string;

  @Field(type => Boolean, { default: v => v ?? false })
  public tokenAccessEnabled?: boolean;

  @Field(type => String)
  public accessTokenForStudents?: string;

  @Field(type => [String])
  public studentCreationMailOverride: Maybe<string[]>;

  @Field(type => Int, { default: v => v ?? 0 })
  public tokenAccessCount?: number;

  @Field(type => Int, { default: v => v ?? 0 })
  public tokenAccessCountMax?: number;

  @Field(type => Boolean, { default: v => v ?? false })
  public tokenAccessSoftLimitMailSent?: boolean;

  @Field(type => Boolean, { default: v => v ?? false })
  public tokenAccessHardLimitMailSent?: boolean;

  @Field(type => String)
  public email: Maybe<string>;

  @Field(type => Boolean)
  public generateLevumiTestReminderForSchuKos: Maybe<boolean>;

  @Field(type => String)
  public osCoachMail: Maybe<string>;

  @Field(type => String)
  public schukoTel: Maybe<string>;

  @Field(type => String)
  public osCoachTel: Maybe<string>;

  @Field(type => [RAGFundingInformation], { default: v => v ?? [] })
  public ragPartnerInformation: Maybe<RAGFundingInformation[]>;

  // Defines the logicstring which in turn builds the register form. Do we ask for a BuT key or an iban for example
  @Field(type => FormOfFinancingLogicExpressionArrayUnion, {
    default: v =>
      v ??
      new FormOfFinancingLogicExpression({
        type: LogicExpression.OR,
        formsOfFinancingArray: [
          new FormOfFinancingLogicExpression({
            type: LogicExpression.AND,
            formsOfFinancingArray: [new FormOfFinancingMitgliedsbeitrag({}), new FormOfFinancingStiftungsgeld({})],
          }),
          new FormOfFinancingBuT({}),
        ],
      }),
  })
  public formOfFinanzingLogicString: Maybe<FormOfFinancingLogicExpression | FormOfFinancing>;

  // Predefined membership fee. Determines the preselected value in the registration form
  @Field(type => Int, { default: v => v ?? 20 })
  public standardMembershipFee: Maybe<number>;

  @Field(type => Int)
  public attendanceLogsCount?: number;

  private static async updatePartnerChangeDate(partnerID: Maybe<string>) {
    if (!partnerID) {
      logger.warn('Couldnt update Partner with id: ' + partnerID);
      return;
    }
    const partnerCollection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
    const partnerToUpdate = await partnerCollection.findOne({ _id: partnerID });

    const partnerConstructed = constructValue(partnerToUpdate) as Partner;
    await partnerConstructed.save();
  }

  static override async beforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: KPF<Product>,
  ): Promise<void> {
    // 1 Check duplicate kpf assignment
    await this.checkDuplicateKpfCombination(update);

    // 2 Check duplicate access token to deconflict future users
    await this._checkDuplicateToken(update);
  }

  private static async _checkDuplicateToken(kpf: KPF) {
    if (!A.isServer) throw new ValidationError('This is a serverside helper only');
    const kpfCollection = await getCollection(KPF._class);
    let count = 0;
    if (kpf.has('accessTokenForExternals') && kpf.accessTokenForExternals) {
      count = await kpfCollection.countDocuments({
        _id: { $ne: kpf._id },
        $or: [
          { accessTokenForExternals: kpf.accessTokenForExternals },
          { accessTokenForStudents: kpf.accessTokenForExternals },
        ],
      });
      if (count >= 1) throw new ValidationError(`Der Token für externe Lehrkräfte ist bereits vergeben.`);
    }
    if (kpf.has('accessTokenForStudents') && kpf.accessTokenForStudents) {
      count = await kpfCollection.countDocuments({
        _id: { $ne: kpf._id },
        $or: [
          { accessTokenForExternals: kpf.accessTokenForStudents },
          { accessTokenForStudents: kpf.accessTokenForStudents },
        ],
      });
      if (count >= 1) throw new ValidationError(`Der Token für Schüler:innen ist bereits vergeben.`);
    }
    if (
      kpf.has('accessTokenForExternals') &&
      kpf.has('accessTokenForStudents') &&
      kpf.accessTokenForStudents &&
      kpf.accessTokenForExternals
    ) {
      count = await kpfCollection.countDocuments({
        _id: kpf._id,
        $or: [
          { accessTokenForExternals: kpf.accessTokenForStudents },
          { accessTokenForStudents: kpf.accessTokenForExternals },
        ],
      });
      if (count >= 1) throw new ValidationError(`Die Token innerhalb eines KPFs müssen unterschiedlich sein.`);
    }
    return true;
  }

  private static async _sendTokenUpdateInfo(newEntity: KPF<Product>, oldEntity?: PartialEntityData<KPF<Product>>) {
    try {
      const semanticCollection = await getRawCollection<EntityData<SemanticRelationship>>(
        SemanticRelationship._collectionName,
      );

      // TODO do we have more than one person here? Technically we could do it
      const externalRelation = (await semanticCollection.find({ predicateID: Role.EXTERNAL_CONTACT }).toArray()).at(0);
      if (!externalRelation?.subjectID) return;

      if (newEntity.accessTokenForStudents && !oldEntity?.accessTokenForStudents) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Schüler:innen wurde angelegt',
            html: kpfTokenCreationInformation({
              tokenSection: 'Schüler:innen',
              token: newEntity.accessTokenForStudents ?? 'Kein Token gefunden',
              usage: newEntity.tokenAccessCountMax?.toString() ?? '',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      } else if (
        newEntity.accessTokenForStudents &&
        oldEntity?.accessTokenForStudents &&
        oldEntity.accessTokenForStudents !== newEntity.accessTokenForStudents
      ) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Schüler:innen wurde geändert',
            html: kpfTokenChangeInformation({
              tokenSection: 'Schüler:innen',
              token: newEntity.accessTokenForStudents ?? 'Kein Token gefunden',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      }

      // Send Mail if token has changed
      if (newEntity.accessTokenForExternals && !oldEntity?.accessTokenForExternals) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Lehrkräfte wurde angelegt',
            html: kpfTokenCreationInformation({
              tokenSection: 'Lehrkräfte',
              token: newEntity.accessTokenForExternals ?? 'Kein Token gefunden',
              usage: '',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      } else if (
        newEntity.accessTokenForExternals &&
        oldEntity?.accessTokenForExternals &&
        oldEntity.accessTokenForExternals !== newEntity.accessTokenForExternals
      ) {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Der Zugangscode für den CHANCENcampus für Lehrkräfte hat sich geändert',
            html: kpfTokenChangeInformation({
              tokenSection: 'Lehrkräfte',
              token: newEntity.accessTokenForExternals ?? 'Kein Token gefunden',
            }),
          }),
          new Recipient('chancencampus@chancenwerk.org'),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  static override async beforeCreate(entity: KPF<Product>): Promise<void> {
    await super.beforeCreate(entity);
    await this.checkDuplicateKpfCombination(entity);
  }

  static override async afterCreate(entity: KPF<Product>, result: InsertOneResult<Document>): Promise<void> {
    // create courses with data from duerchführungstage and locations. If there isnt any data we create an empty course
    try {
      if (entity.produkt?._class === ProductLKK._class) {
        const LKK = await import('../../../db/courses/CourseLernfoerderung.js');
        await LKK.CourseLernfoerderung.updateAndCreateCoursesOfKPF(entity as unknown as KPF<ProductLKK>);
      } else if (entity.produkt?._class === ProductSCH._class) {
        const SCH = await import('../../../db/courses/CourseChancenschule.js');
        await SCH.CourseChancenschule.updateAndCreateCoursesOfKPF(entity as unknown as KPF<ProductSCH>);
      }
    } catch (e) {
      logger.error(e);
    }

    // sync the change to the partner so it can keep track of changes too
    await this.updatePartnerChangeDate(entity.schoolID);

    // Send Mail if token has changed
    await this._sendTokenUpdateInfo(entity);

    return super.afterCreate(entity, result);
  }

  static override async afterUpdate(
    oldEntity: PartialEntityData<KPF<Product>>,
    newEntity: KPF<Product>,
    result: UpdateResult<Document>,
  ): Promise<void> {
    try {
      // create courses with data from duerchführungstage and locations. If there isnt any data we create an empty course
      if (newEntity.produkt?._class === ProductLKK._class) {
        const LKK = await import('../../../db/courses/CourseLernfoerderung.js');
        const oldKpf = constructValue(oldEntity) as KPF<ProductLKK>;
        await LKK.CourseLernfoerderung.updateAndCreateCoursesOfKPF(newEntity as unknown as KPF<ProductLKK>, oldKpf);
      } else if (newEntity.produkt?._class === ProductSCH._class) {
        const SCH = await import('../../../db/courses/CourseChancenschule.js');
        const oldKpf = constructValue(oldEntity) as KPF<ProductSCH>;
        await SCH.CourseChancenschule.updateAndCreateCoursesOfKPF(newEntity as unknown as KPF<ProductSCH>, oldKpf);
      }
    } catch (e) {
      logger.error(e);
    }

    // sync the change to the partner so it can keep track of changes too
    await this.updatePartnerChangeDate(newEntity.schoolID);

    // Send Mail if token has changed
    await this._sendTokenUpdateInfo(newEntity, oldEntity);

    return super.afterUpdate(oldEntity, newEntity, result);
  }

  private static async checkDuplicateKpfCombination(kpf: KPF<Product>) {
    if (!A.isServer) throw new ValidationError('This is a serverside helper only');

    const kpfCollection = await getRawCollection<EntityData<KPF>>(KPF._collectionName);
    const count = await kpfCollection.countDocuments({
      _id: { $ne: kpf._id },
      schoolID: kpf.schoolID,
      'produkt._class': kpf.produkt?._class,
    });
    if (count >= 1) throw new ValidationError(`Diese Produkt-Schule-Kombination gibt es bereits.`);
    return true;
  }

  static override async afterRemove(id: string, entityData: any): Promise<void> {
    await super.afterRemove(id, entityData);
    const relationshipCollection = await getRawCollection(SemanticRelationship._collectionName);
    await relationshipCollection.deleteMany({ $or: [{ objectID: id }, { subjectID: id }] });
  }

  override toString() {
    return [this.kuerzel, this.school?.name].filter(v => v).join(' ');
  }
}
